import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getShortlistData } from "../../store/slices/intrestandshortlisted";
import { Container } from "react-bootstrap";

function scoreColor(score) {
  if (!score && score !== 0) return { bg: "#fef9ec", border: "#f5c842", text: "#b07820" };
  if (score >= 90) return { bg: "#fef9ec", border: "#f5c842", text: "#b07820" };
  if (score >= 80) return { bg: "#f0fdf4", border: "#86efac", text: "#16a34a" };
  return { bg: "#fdf6f0", border: "#f0c0a0", text: "#b05020" };
}

function computeAge(dob) {
  if (!dob) return null;
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

// ── List Card ──
function ProfileCard({ entry, onRemove, removing, navigate, type }) {
  const person = type === "byMe" ? entry?.targetUserId : entry?.userId;

  const targetId = person?._id;
  const photoUrl = person?.profilePhoto;
  const name = person?.name || "—";
  const age = person?.age || (person?.dateOfBirth ? computeAge(person.dateOfBirth) : null);
  const height = person?.height || "—";
  const maritalStatus = person?.maritalStatus || "—";
  const education = person?.education || person?.highestEducation || "—";
  const occupation = person?.occupation || person?.employedIn || "—";
  const location = person?.city || person?.location || person?.state || "—";
  const income = person?.annualIncome || person?.income || "—";
  const caste = person?.caste || person?.subCaste || "—";
  const matchScore = person?.matchScore;
  const isOnline = person?.isOnline || false;
  const isVerified = person?.isVerified || false;

  const savedOn = entry?.createdAt
    ? new Date(entry.createdAt).toLocaleDateString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
    })
    : "—";

  const sc = scoreColor(matchScore);

  return (
    <div className={`sl-card${removing.has(entry._id) ? " removing" : ""}`}>
      <div className="sl-star-ribbon" />

      <div className="sl-card-photo">
        <img
          src={photoUrl || "/placeholder.jpg"}
          alt={name}
          onError={(e) => { e.target.src = "/placeholder.jpg"; }}
        />
        {isOnline && <div className="online-pill">● Online</div>}
        {isVerified && <div className="verified-pill">✓ Verified</div>}
      </div>

      <div className="sl-card-body">
        <div className="sl-card-top">
          <div style={{ flex: 1 }}>
            <div className="sl-card-name">{name}</div>
            <div className="sl-card-meta">
              {age ? `${age} yrs` : ""}
              {age && height !== "—" ? " · " : ""}
              {height !== "—" ? height : ""}
              {maritalStatus !== "—" ? ` · ${maritalStatus}` : ""}
            </div>
          </div>
          {matchScore != null && (
            <div className="match-ring" style={{ background: sc.bg, border: `2px solid ${sc.border}` }}>
              <div className="match-ring-num" style={{ color: sc.text }}>{matchScore}%</div>
              <div className="match-ring-lbl">match</div>
            </div>
          )}
        </div>

        <div className="chip-row">
          {education !== "—" && <span className="chip">🎓 {education}</span>}
          {occupation !== "—" && <span className="chip">💼 {occupation}</span>}
          {location !== "—" && <span className="chip">📍 {location}</span>}
        </div>

        <div className="sl-detail-grid">
          {income !== "—" && (
            <div className="sl-detail-item">
              <div className="sl-detail-lbl">Income</div>
              <div className="sl-detail-val">{income}</div>
            </div>
          )}
          {caste !== "—" && (
            <div className="sl-detail-item">
              <div className="sl-detail-lbl">Caste</div>
              <div className="sl-detail-val">{caste}</div>
            </div>
          )}
          <div className="sl-detail-item">
            <div className="sl-detail-lbl">Saved On</div>
            <div className="sl-detail-val" style={{ fontSize: 12 }}>{savedOn}</div>
          </div>
        </div>

        <div className="sl-card-actions">
          {type === "byMe" && <button className="btn-interest">Send Interest</button>}
          <button className="btn-view" onClick={() => navigate(`/user/profile-details/${targetId}`)}>
            View Profile
          </button>
          {type === "byMe" && (
            <button className="btn-remove" onClick={() => onRemove(entry._id)}>✕ Remove</button>
          )}
          <button className="btn-msg" title="Message" onClick={() => navigate("/user/message", { state: { userId: targetId } })}>
            💬
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Grid Card ──
function GridProfileCard({ entry, onRemove, removing, navigate, type }) {
  const person = type === "byMe" ? entry?.targetUserId : entry?.userId;

  const targetId = person?._id;
  const photoUrl = person?.profilePhoto;
  const name = person?.name || "—";
  const age = person?.age || (person?.dateOfBirth ? computeAge(person.dateOfBirth) : null);
  const occupation = person?.occupation || person?.employedIn || "—";
  const education = person?.education || person?.highestEducation || "—";
  const location = person?.city || person?.location || person?.state || "—";
  const matchScore = person?.matchScore;
  const isOnline = person?.isOnline || false;
  const sc = scoreColor(matchScore);

  const savedOn = entry?.createdAt
    ? new Date(entry.createdAt).toLocaleDateString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
    })
    : "—";

  return (
    <>
      <Container>
        <div className={`sg-card${removing.has(entry._id) ? " removing" : ""}`}>
          <div className="sg-star-corner" />
          <div className="sg-photo-wrap">
            <img
              src={photoUrl || "/placeholder.jpg"}
              alt={name}
              onError={(e) => { e.target.src = "/placeholder.jpg"; }}
            />
            <div className="sg-photo-overlay" />
            {isOnline && <div className="sg-online">● Online</div>}
            <div className="sg-name-over">
              <div className="sg-name">{name}</div>
              <div className="sg-meta">
                {age ? `${age} yrs` : ""}
                {age && location !== "—" ? " · " : ""}
                {location !== "—" ? location : ""}
              </div>
            </div>
          </div>

          <div className="sg-body">
            <div className="sg-info-row">
              <div className="sg-chips">
                {occupation !== "—" && <span className="sg-chip">{occupation}</span>}
                {education !== "—" && <span className="sg-chip">{education}</span>}
              </div>
              {matchScore != null && (
                <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 8 }}>
                  <div className="sg-match" style={{ color: sc.text }}>{matchScore}%</div>
                  <div className="sg-match-lbl">match</div>
                </div>
              )}
            </div>

            <div style={{ fontSize: 11, color: "#b09090", display: "flex", alignItems: "center", gap: 4 }}>
              ★ Saved {savedOn}
            </div>

            <div className="sg-actions">
              <button className="sg-btn-primary" onClick={() => navigate("/user/profile-details", { state: { userId: targetId } })}>
                View Profile
              </button>
              <button className="sg-btn-icon" title="Message" onClick={() => navigate("/user/message", { state: { userId: targetId } })}>
                💬
              </button>
              {type === "byMe" && (
                <button className="sg-btn-icon remove" title="Remove" onClick={() => onRemove(entry._id)}>✕</button>
              )}
            </div>
          </div>
        </div>
      </Container>
    </>
  );
}

// ── Main Page ──
export default function Shortlist() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState("byMe");
  const [removing, setRemoving] = useState(new Set());
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("saved");
  const [viewMode, setViewMode] = useState("list");

  const { shortlistedData = {}, loading } = useSelector((state) => state.interestShortlist);

  const shortlistedByMe = shortlistedData?.shortlistedByMe || [];

  const shortlistedMe = shortlistedData?.shortlistedMe || [];

  useEffect(() => { dispatch(getShortlistData()); }, [dispatch]);

  const handleRemove = (id) => {
    setRemoving((prev) => new Set(prev).add(id));
    setTimeout(() => {
      setRemoving((prev) => { const s = new Set(prev); s.delete(id); return s; });
    }, 280);
  };

  const rawList = activeTab === "byMe" ? shortlistedByMe : shortlistedMe;


  const filtered = rawList.filter((entry) => {
    const person = activeTab === "byMe" ? entry?.targetUserId : entry?.userId;
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return (
      (person?.name || "").toLowerCase().includes(q) ||
      (person?.occupation || "").toLowerCase().includes(q) ||
      (person?.employedIn || "").toLowerCase().includes(q) ||
      (person?.city || "").toLowerCase().includes(q) ||
      (person?.location || "").toLowerCase().includes(q) ||
      (person?.state || "").toLowerCase().includes(q)
    );
  });

  // Sort
  const activeList = [...filtered].sort((a, b) => {
    if (sortBy === "name") {
      const pA = activeTab === "byMe" ? a?.targetUserId : a?.userId;
      const pB = activeTab === "byMe" ? b?.targetUserId : b?.userId;
      return (pA?.name || "").localeCompare(pB?.name || "");
    }
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  return (
    <>
      <Container fluid>
        <div className="sl-root">
          {/* HEADER */}
          <div className="sl-header">
            <div className="sl-title">
              Shortlisted Profiles <span className="sl-title-star">★</span>
            </div>
            <div className="sl-subtitle">Profiles you've saved for closer consideration</div>
          </div>

          {loading ? (
            <>

              <div className="sl-stats">
                {[0, 1].map((i) => (
                  <div key={i} className="stat-card ep-skeleton-card" style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div className="ep-skeleton-circle" style={{ width: 44, height: 44, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div className="ep-skeleton-title" style={{ width: "40%", marginBottom: 6 }} />
                      <div className="ep-skeleton-line short" style={{ width: "65%" }} />
                    </div>
                  </div>
                ))}
              </div>


              <div className="sl-tabs">
                <div className="ep-skeleton-line" style={{ width: 160, height: 36, borderRadius: 8 }} />
                <div className="ep-skeleton-line" style={{ width: 140, height: 36, borderRadius: 8, marginLeft: 8 }} />
              </div>


              <div className="sl-toolbar">
                <div className="ep-skeleton-line" style={{ flex: 1, height: 38, borderRadius: 8 }} />
                <div className="ep-skeleton-line" style={{ width: 120, height: 38, borderRadius: 8 }} />
                <div style={{ display: "flex", gap: 6 }}>
                  <div className="ep-skeleton-circle" style={{ width: 38, height: 38, borderRadius: 6 }} />
                  <div className="ep-skeleton-circle" style={{ width: 38, height: 38, borderRadius: 6 }} />
                </div>
              </div>


              <div className="sl-list">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="ep-skeleton-card" style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                    {/* Photo */}
                    <div className="ep-skeleton-avatar" style={{ width: 130, height: 160, flexShrink: 0, borderRadius: 10 }} />

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                        <div className="ep-skeleton-title" style={{ width: "35%" }} />
                        <div className="ep-skeleton-line" style={{ width: 80, height: 24, borderRadius: 20 }} />
                      </div>
                      <div className="ep-skeleton-line short" style={{ width: "55%", marginBottom: 8 }} />
                      {/* Chips */}
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                        <div className="ep-skeleton-line" style={{ width: 75, height: 24, borderRadius: 20 }} />
                        <div className="ep-skeleton-line" style={{ width: 90, height: 24, borderRadius: 20 }} />
                        <div className="ep-skeleton-line" style={{ width: 65, height: 24, borderRadius: 20 }} />
                      </div>
                      {/* Action buttons */}
                      <div style={{ display: "flex", gap: 8 }}>
                        <div className="ep-skeleton-line" style={{ width: 100, height: 32, borderRadius: 6 }} />
                        <div className="ep-skeleton-line" style={{ width: 90, height: 32, borderRadius: 6 }} />
                        <div className="ep-skeleton-line" style={{ width: 80, height: 32, borderRadius: 6 }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>

              <div className="sl-stats">
                {[
                  { icon: "★", bg: "#fef9ec", num: shortlistedByMe.length, lbl: "Shortlisted By Me" },
                  { icon: "♡", bg: "#fde8f0", num: shortlistedMe.length, lbl: "Shortlisted Me" },
                ].map(({ icon, bg, num, lbl }) => (
                  <div key={lbl} className="stat-card">
                    <div className="stat-icon" style={{ background: bg }}>{icon}</div>
                    <div className="stat-info">
                      <div className="stat-num">{num}</div>
                      <div className="stat-lbl">{lbl}</div>
                    </div>
                  </div>
                ))}
              </div>


              <div className="sl-tabs">
                <button className={`sl-tab-btn${activeTab === "byMe" ? " active" : ""}`} onClick={() => setActiveTab("byMe")}>
                  Shortlisted By Me <span className="sl-tab-count">{shortlistedByMe.length}</span>
                </button>
                <button className={`sl-tab-btn${activeTab === "byThem" ? " active" : ""}`} onClick={() => setActiveTab("byThem")}>
                  Shortlisted Me <span className="sl-tab-count">{shortlistedMe.length}</span>
                </button>
              </div>


              <div className="sl-toolbar">
                <input
                  className="sl-search"
                  placeholder="Search by name, occupation, location…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <select className="sl-sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="saved">Date Saved</option>
                  <option value="name">Name A–Z</option>
                </select>
                <div className="sl-view-toggle">
                  <button className={`sl-view-btn${viewMode === "list" ? " active" : ""}`} onClick={() => setViewMode("list")} title="List view">☰</button>
                  <button className={`sl-view-btn${viewMode === "grid" ? " active" : ""}`} onClick={() => setViewMode("grid")} title="Grid view">⊞</button>
                </div>
              </div>


              {activeList.length === 0 && (
                <div className="sl-empty">
                  <div className="sl-empty-icon">★</div>
                  <div className="sl-empty-title">
                    {search
                      ? "No profiles match your search"
                      : activeTab === "byMe"
                        ? "You haven't shortlisted anyone yet"
                        : "Nobody has shortlisted you yet"}
                  </div>
                  <div className="sl-empty-sub">
                    {search
                      ? "Try a different name or location."
                      : activeTab === "byMe"
                        ? "Browse recommendations and tap ☆ Shortlist to save profiles here."
                        : "Keep your profile updated to attract more interest!"}
                  </div>
                  {!search && activeTab === "byMe" && (
                    <button className="sl-empty-btn" onClick={() => navigate("/user/recommendations")}>
                      Browse Recommendations →
                    </button>
                  )}
                </div>
              )}

              {/* LIST VIEW */}
              {viewMode === "list" && activeList.length > 0 && (
                <div className="sl-list">
                  {activeList.map((entry) => (
                    <ProfileCard
                      key={entry._id}
                      entry={entry}
                      onRemove={handleRemove}
                      removing={removing}
                      navigate={navigate}
                      type={activeTab === "byMe" ? "byMe" : "byThem"}
                    />
                  ))}
                </div>
              )}

              {/* GRID VIEW */}
              {viewMode === "grid" && activeList.length > 0 && (
                <div className="sl-grid">
                  {activeList.map((entry) => (
                    <GridProfileCard
                      key={entry._id}
                      entry={entry}
                      onRemove={handleRemove}
                      removing={removing}
                      navigate={navigate}
                      type={activeTab === "byMe" ? "byMe" : "byThem"}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </Container>
    </>
  );
}